import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { Typography } from '../../components/common/Typography/Typography';
import { env } from '../../config/env';
import { EXTERNAL_LINKS } from '../../constants/links';
import { APP_ROUTES } from '../../constants/routes';
import { useTranslation } from '../../hooks/useTranslation/useTranslation';
import styles from './MainLayout.module.css';
import { MainLayoutProps } from './MainLayout.types';

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const isModsActive =
    location.pathname === APP_ROUTES.MODS ||
    location.pathname === APP_ROUTES.VR_MODS ||
    location.pathname === APP_ROUTES.UEVR_MODS ||
    location.pathname === APP_ROUTES.HEAD_TRACKING_MODS ||
    location.pathname.startsWith('/profile');

  const isHomeActive = location.pathname === APP_ROUTES.HOME;
  const isGamesActive =
    location.pathname === APP_ROUTES.GAMES || location.pathname.startsWith(`${APP_ROUTES.GAMES}/`);
  const isSimrLabsActive = location.pathname === APP_ROUTES.SIMRLABS;

  return (
    <div className={styles.layout}>
      {/* Navbar Header */}
      <header className={styles.navbar}>
        <div className={styles.navbarContainer}>
          <Link to={APP_ROUTES.HOME} className={styles.brand}>
            <img src={`${env().PUBLIC_URL}/favicon.png`} alt="" className={styles.brandLogo} />
            <span className={styles.brandText}>{t.brand}</span>
          </Link>

          <div className={styles.navRight}>
            {/* Navigation Tabs */}
            <nav className={styles.navTabs} aria-label={t.nav.mainNavigation}>
              <Link
                to={APP_ROUTES.HOME}
                className={`${styles.navTab} ${isHomeActive ? styles.navTabActive : ''}`}
                aria-current={isHomeActive ? 'page' : undefined}
              >
                {t.nav.home}
              </Link>
              <Link
                to={APP_ROUTES.GAMES}
                className={`${styles.navTab} ${isGamesActive ? styles.navTabActive : ''}`}
                aria-current={isGamesActive ? 'page' : undefined}
              >
                {t.nav.games}
              </Link>
              <Link
                to={APP_ROUTES.MODS}
                className={`${styles.navTab} ${isModsActive ? styles.navTabActive : ''}`}
                aria-current={isModsActive ? 'page' : undefined}
              >
                {t.nav.mods}
              </Link>
              <Link
                to={APP_ROUTES.SIMRLABS}
                className={`${styles.navTab} ${isSimrLabsActive ? styles.navTabActive : ''}`}
                aria-current={isSimrLabsActive ? 'page' : undefined}
              >
                {t.nav.simrlabs}
              </Link>
            </nav>

            {/* External Social Links */}
            <div className={styles.socialLinks} aria-label={t.nav.communityLinks}>
              <a
                href={EXTERNAL_LINKS.YOUTUBE_CHANNEL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.socialLink} ${styles.socialLinkYoutube}`}
                title={t.nav.youtubeChannel}
                aria-label={t.nav.youtubeChannel}
              >
                <svg
                  className={styles.youtubeIcon}
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                <span className={styles.socialLinkLabel}>{t.nav.youtubeChannel}</span>
              </a>
              <a
                href={EXTERNAL_LINKS.YOUTUBE_COMMUNITY}
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.socialLink} ${styles.socialLinkCommunity}`}
                title={t.nav.youtubeCommunity}
                aria-label={t.nav.youtubeCommunity}
              >
                <svg
                  className={styles.communityIcon}
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 7V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z" />
                </svg>
                <span className={styles.socialLinkLabel}>{t.nav.youtubeCommunity}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className={styles.main} id="main-content">
        {children || <Outlet />}
      </main>

      {/* Footer Section */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Typography variant="body2">{t.footer.copy({ year: currentYear })}</Typography>
          <Link to={APP_ROUTES.PRIVACY} className={styles.footerLink}>
            {t.footer.privacy}
          </Link>
        </div>
      </footer>
    </div>
  );
};
